// LT-643 — client-side PDF generation for the campaign completion report.
//
// Builds a selectable-text, Agent-Noda-branded PDF from the stored report
// payload (the same deterministic metrics + LLM narrative the UI renders).
// No server PDF toolchain — jsPDF + autotable run in the browser. The report
// contains contact PII by design; the caller shows a warning before invoking.
//
// Palette mirrors the app design system (src/index.css): cyan accent on dark
// navy, green/red for KPI status — not generic blue/white. All text is
// sanitized to the WinAnsi range jsPDF's standard fonts can render.
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

import type {
  CompletionReportNarrative,
  CompletionReportPayload,
} from '../../../services/campaignsAPI';

// DS tokens (src/index.css) as RGB triples.
const NAVY: [number, number, number] = [15, 23, 42]; // --surface-1 #0f172a
const ACCENT: [number, number, number] = [6, 182, 212]; // --accent #06b6d4
const OK: [number, number, number] = [16, 185, 129]; // --ok #10b981
const DANGER: [number, number, number] = [239, 68, 68]; // --danger #ef4444
const SLATE: [number, number, number] = [51, 65, 85]; // body
const MUTED: [number, number, number] = [100, 116, 139]; // muted

const MARGIN = 42;
const FOOTER_RESERVE = 46;

// Disposition / temperature glossary — sourced from the call-analysis taxonomy
// (backend/services/call_analysis_service.py: VALID_DISPOSITIONS,
// STATUS_TO_DISPOSITION, VALID_TEMPERATURES).
const DISPOSITION_GLOSSARY: Array<[string, string]> = [
  ['Qualified', 'converted, interested_meeting, interested_referred'],
  ['Callback', 'interested_callback'],
  ['Nurture', 'interested_future, interested_info, soft_rejection, not_available'],
  ['Closed out', 'hard_rejection'],
  ['Not reached', 'no_answer, voicemail, busy, wrong_number, gatekeeper_block'],
];
const TEMPERATURE_GLOSSARY: Array<[string, string]> = [
  ['hot', 'Strong buying signal — ready to progress.'],
  ['warm', 'Genuine interest; needs follow-up.'],
  ['lukewarm', 'Mild interest / non-committal.'],
  ['cold', 'Little interest or wrong fit.'],
  ['dead', 'No interest, invalid, or do-not-contact.'],
];

const _UNICODE_MAP: Record<string, string> = {
  '‘': "'", '’': "'", '‚': "'", '‛': "'",
  '“': '"', '”': '"', '„': '"', '‟': '"',
  '–': '-', '—': '-', '−': '-', '‐': '-', '‑': '-',
  '…': '...', '•': '-', '·': '-', '‧': '-',
  '≤': '<=', '≥': '>=', '≠': '!=', '≈': '~',
  '→': '->', '←': '<-', '⇒': '=>',
  '×': 'x', '÷': '/', ' ': ' ', ' ': ' ', ' ': ' ',
  '​': '', '﻿': '', '™': '(TM)', '®': '(R)', '©': '(C)',
};

/** Map smart punctuation to ASCII and drop any char jsPDF's standard fonts
 * can't render (> Latin-1), which otherwise corrupts kerning/spacing. */
function toText(s: string | null | undefined): string {
  let out = '';
  for (const ch of String(s ?? '')) {
    if (_UNICODE_MAP[ch] !== undefined) out += _UNICODE_MAP[ch];
    else if (ch.charCodeAt(0) <= 0xff) out += ch;
    else out += ''; // drop characters outside WinAnsi
  }
  return out;
}

function stripMd(s: string | null | undefined): string {
  return toText(s)
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/^#+\s*/gm, '')
    .trim();
}

function fmtNum(n: number | null | undefined): string {
  if (n === null || n === undefined) return '-';
  return Number(n).toLocaleString();
}

function fmtDate(iso: string | null | undefined, withTime = false): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '-';
  const date = d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  if (!withTime) return date;
  return `${date} ${d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}`;
}

function tableFinalY(doc: jsPDF, fallback: number): number {
  const withTable = doc as unknown as { lastAutoTable?: { finalY: number } };
  return withTable.lastAutoTable?.finalY ?? fallback;
}

interface PdfOptions {
  logoDataUrl?: string | null;
}

export function buildCompletionReportPdf(
  report: CompletionReportPayload,
  opts: PdfOptions = {},
): jsPDF {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const contentW = pageW - MARGIN * 2;
  let y = MARGIN;

  const setFill = (c: [number, number, number]) => doc.setFillColor(c[0], c[1], c[2]);
  const setText = (c: [number, number, number]) => doc.setTextColor(c[0], c[1], c[2]);

  const ensure = (need: number) => {
    if (y + need > pageH - FOOTER_RESERVE) {
      doc.addPage();
      y = MARGIN;
    }
  };

  const heading = (text: string, opt: { size?: number; accent?: boolean; gapTop?: number } = {}) => {
    const size = opt.size ?? 12;
    y += opt.gapTop ?? 8;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(size);
    setText(opt.accent ? NAVY : NAVY);
    const lines = doc.splitTextToSize(stripMd(text), contentW) as string[];
    for (const line of lines) {
      ensure(size + 6);
      doc.text(line, MARGIN, y);
      y += size + 4;
    }
    // accent underline rule
    ensure(6);
    setFill(ACCENT);
    doc.rect(MARGIN, y - 2, 26, 2, 'F');
    y += 8;
  };

  const para = (text: string, opt: { size?: number; color?: [number, number, number]; bold?: boolean } = {}) => {
    const clean = stripMd(text);
    if (!clean) return;
    const size = opt.size ?? 10;
    doc.setFont('helvetica', opt.bold ? 'bold' : 'normal');
    doc.setFontSize(size);
    setText(opt.color ?? SLATE);
    const lines = doc.splitTextToSize(clean, contentW) as string[];
    for (const line of lines) {
      ensure(size + 4);
      doc.text(line, MARGIN, y);
      y += size + 4;
    }
    y += 4;
  };

  const labelled = (label: string, value: string | null | undefined) => {
    const v = toText(value).trim();
    if (!v) return;
    const size = 10;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(size);
    setText(MUTED);
    const labelW = doc.getTextWidth(label + '  ');
    const lines = doc.splitTextToSize(v, contentW - labelW) as string[];
    ensure(size + 4);
    doc.text(label, MARGIN, y);
    doc.setFont('helvetica', 'normal');
    setText(SLATE);
    doc.text(lines[0], MARGIN + labelW, y);
    y += size + 4;
    for (const line of lines.slice(1)) {
      ensure(size + 4);
      doc.text(line, MARGIN + labelW, y);
      y += size + 4;
    }
    y += 2;
  };

  const table = (
    head: string[],
    body: Array<Array<string | number>>,
    statusColIndex?: number,
  ) => {
    if (body.length === 0) return;
    autoTable(doc, {
      startY: y,
      head: [head.map((h) => toText(h))],
      body: body.map((r) => r.map((c) => (typeof c === 'number' ? c : toText(String(c))))),
      margin: { left: MARGIN, right: MARGIN, top: MARGIN, bottom: FOOTER_RESERVE },
      styles: { fontSize: 9, cellPadding: 5, textColor: SLATE, lineColor: [226, 232, 240], lineWidth: 0.5 },
      headStyles: { fillColor: NAVY, textColor: [255, 255, 255], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      didParseCell: (data) => {
        if (statusColIndex !== undefined && data.section === 'body' && data.column.index === statusColIndex) {
          const raw = String(data.cell.raw || '');
          if (/met/i.test(raw)) data.cell.styles.textColor = OK;
          else if (/miss/i.test(raw)) data.cell.styles.textColor = DANGER;
          data.cell.styles.fontStyle = 'bold';
        }
      },
    });
    y = tableFinalY(doc, y) + 14;
  };

  const campaign = report.campaign;
  const metrics = report.metrics;
  const narrative = report.narrative;

  // ---- Branded header band (page 1) -----------------------------------
  const bandH = 58;
  setFill(NAVY);
  doc.rect(0, 0, pageW, bandH, 'F');
  let bx = MARGIN;
  if (opts.logoDataUrl) {
    try {
      doc.addImage(opts.logoDataUrl, 'PNG', MARGIN, 16, 26, 26);
      bx = MARGIN + 36;
    } catch {
      bx = MARGIN;
    }
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(255, 255, 255);
  doc.text('Agent Noda', bx, 28);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  setText(ACCENT);
  doc.text('CAMPAIGN COMPLETION REPORT', bx, 42);
  y = bandH + 22;

  // ---- Campaign header -------------------------------------------------
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  setText(NAVY);
  const titleLines = doc.splitTextToSize(toText(campaign?.name) || 'Campaign', contentW) as string[];
  for (const line of titleLines) {
    ensure(22);
    doc.text(line, MARGIN, y);
    y += 22;
  }
  // status + run dates sub-line
  const statusLabel = (campaign?.status || '').replace(/_/g, ' ');
  const runFrom = fmtDate(campaign?.launched_at);
  const runTo = fmtDate(campaign?.ended_at);
  para(
    `${statusLabel}  ·  Run: ${runFrom} - ${runTo}  ·  Generated ${fmtDate(report.generated_at, true)}`,
    { size: 9, color: MUTED },
  );
  if (campaign?.status === 'force_ended') {
    para(
      `This campaign was ended early.${campaign.end_reason ? ` Reason: ${campaign.end_reason}` : ''}`,
      { size: 10, color: DANGER, bold: true },
    );
  }
  labelled('Purpose:', campaign?.purpose);
  labelled('Audience:', campaign?.target_audience);
  labelled('Goal:', campaign?.campaign_goals);

  // ---- Narrative: headline + executive summary -------------------------
  if (narrative?.headline) heading(narrative.headline, { size: 13, gapTop: 10 });
  if (narrative?.executive_summary) para(narrative.executive_summary);

  // ---- Overview --------------------------------------------------------
  if (metrics) {
    const ov = metrics.overview;
    heading('Overview');
    table(
      ['Metric', 'Value'],
      [
        ['Contacts enrolled', fmtNum(ov.total_enrolments)],
        ['Calls attempted', fmtNum(ov.calls_attempted)],
        ['Calls connected', `${fmtNum(ov.calls_connected)} (${ov.connect_rate_pct}%)`],
        ['Qualified', `${fmtNum(ov.qualified_count)} (${ov.conversion_rate_pct}%)`],
        ['Opted out', fmtNum(ov.opted_out)],
        ['Callbacks requested', fmtNum(ov.callbacks_requested)],
        ['Pipeline value', fmtNum(ov.pipeline_dollars)],
      ],
    );

    if (metrics.kpi_scorecard.length > 0) {
      heading('KPI scorecard');
      table(
        ['KPI', 'Target', 'Actual', 'Status'],
        metrics.kpi_scorecard.map((k) => [
          k.name,
          `${fmtNum(k.target_value)}${k.unit}`,
          k.computed ? `${fmtNum(k.current_value)}${k.unit}` : 'n/a',
          k.attained === null ? '-' : k.attained ? 'Met' : 'Missed',
        ]),
        3,
      );
    }

    const disp = Object.entries(metrics.call_outcomes.by_disposition);
    if (disp.length > 0) {
      heading('Call dispositions');
      table(['Disposition', 'Count'], disp.map(([k, v]) => [k.replace(/_/g, ' '), fmtNum(v)]));
    }
    const temp = Object.entries(metrics.call_outcomes.by_temperature);
    if (temp.length > 0) {
      heading('Lead temperature');
      table(['Temperature', 'Count'], temp.map(([k, v]) => [k, fmtNum(v)]));
    }

    if (metrics.captured_data.length > 0) {
      heading('Captured data');
      table(
        ['Field', 'Filled', 'Summary'],
        metrics.captured_data.map((f) => {
          const r = f.rollup as Record<string, unknown>;
          let summary = '';
          if (typeof r.sum === 'number') summary = `sum ${r.sum}, avg ${r.avg}`;
          else if (r.by_value && typeof r.by_value === 'object') {
            summary = Object.entries(r.by_value as Record<string, number>).map(([k, v]) => `${k}: ${v}`).join(', ');
          } else if (typeof r.total_items === 'number') summary = `${r.total_items} items`;
          return [f.label, `${r.filled ?? 0}/${r.total_calls ?? 0}`, summary];
        }),
      );

      // Itemized rows for list-type fields (e.g. an order). rollup.items is
      // bounded server-side; show a "+N more" note when truncated.
      for (const f of metrics.captured_data) {
        const r = f.rollup as Record<string, unknown>;
        const items = Array.isArray(r.items) ? (r.items as Array<Record<string, unknown>>) : [];
        if (items.length === 0) continue;
        const declared = (Array.isArray(r.item_columns) ? r.item_columns : []) as Array<{ key: string; label: string }>;
        const colDefs = declared.length > 0
          ? declared
          : Array.from(
              items.reduce<Set<string>>((acc, it) => { Object.keys(it).forEach((k) => acc.add(k)); return acc; }, new Set()),
            ).map((k) => ({ key: k, label: k }));
        heading(`${f.label} - items`, { size: 11 });
        table(
          colDefs.map((c) => c.label),
          items.map((it) => colDefs.map((c) => {
            const v = it[c.key];
            return v === null || v === undefined ? '' : String(v);
          })),
        );
        if (r.items_truncated === true && typeof r.total_items === 'number') {
          para(`Showing ${items.length} of ${r.total_items} items.`, { size: 8, color: MUTED });
        }
      }
    }
  }

  // ---- Glossary --------------------------------------------------------
  heading('Definitions');
  para(
    'Disposition - the classified outcome of each call: for connected calls, the result of the conversation; for calls that did not connect, the reason.',
    { size: 9, color: MUTED },
  );
  table(['Disposition group', 'Values'], DISPOSITION_GLOSSARY.map(([g, v]) => [g, v]));
  para(
    'Lead temperature - the AI’s read of how promising the lead is after the call (coldest to hottest).',
    { size: 9, color: MUTED },
  );
  table(['Temperature', 'Meaning'], TEMPERATURE_GLOSSARY.map(([t, m]) => [t, m]));

  // ---- Narrative detail sections ---------------------------------------
  const sections: Array<[keyof CompletionReportNarrative, string]> = [
    ['kpi_assessment', 'KPI assessment'],
    ['what_worked', 'What worked'],
    ['what_to_improve', 'What to improve'],
    ['recommended_next_steps', 'Recommended next steps'],
  ];
  if (narrative) {
    for (const [key, title] of sections) {
      if (narrative[key]) {
        heading(title);
        para(narrative[key]);
      }
    }
  }

  // ---- Priority contacts (PII) -----------------------------------------
  if (metrics && metrics.priority_contacts.length > 0) {
    heading('Priority follow-up contacts');
    table(
      ['Name', 'Phone', 'Temp', 'Disposition', 'Follow-up'],
      metrics.priority_contacts.map((c) => [
        c.name,
        c.phone || '-',
        c.temperature || '-',
        (c.disposition || '-').replace(/_/g, ' '),
        c.follow_up_action_type
          ? `${c.follow_up_action_type}${c.follow_up_description ? `: ${c.follow_up_description}` : ''}`
          : '-',
      ]),
    );
  }

  // ---- Footer on every page -------------------------------------------
  const pageCount = doc.getNumberOfPages();
  const footerName = toText(campaign?.name) || 'Campaign';
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    const fy = pageH - 24;
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.5);
    doc.line(MARGIN, fy - 8, pageW - MARGIN, fy - 8);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    setText(MUTED);
    doc.text(`Agent Noda  ·  ${footerName}`, MARGIN, fy);
    doc.text(`Page ${i} of ${pageCount}`, pageW - MARGIN, fy, { align: 'right' });
  }

  return doc;
}

/** Best-effort fetch of the Agent Noda mark (served from /brand) as a data URL
 * for embedding in the PDF header. Returns null on any failure. */
async function loadLogoDataUrl(): Promise<string | null> {
  try {
    const res = await fetch('/brand/agent-noda-mark-192.png');
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

export async function downloadCompletionReportPdf(report: CompletionReportPayload): Promise<void> {
  const logoDataUrl = await loadLogoDataUrl();
  const doc = buildCompletionReportPdf(report, { logoDataUrl });
  const safeName = (report.campaign?.name || 'campaign')
    .replace(/[^a-z0-9]+/gi, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();
  doc.save(`${safeName || 'campaign'}_completion_report.pdf`);
}
